/** Grubun tüm satırlarına toplama kuralı (`breakdown.rollup`). */
export function setSuggestionRollup(
  mapping: readonly KpiMappingRow[],
  key: string,
  rollup: Rollup,
): KpiMappingRow[] {
  return mapping.map((r) =>
    isSuggestionRow(r) && suggestionKey(r.breakdown) === key
      ? { ...r, breakdown: { ...r.breakdown, rollup } }
      : r,
  );
}

/**
 * Aynı üstte bu öneri DIŞINDA hesap grubu var mı: setteki hesap grubu adayı grup (bu boyut hariç)
 * ya da aynı üstte kabul edilmiş, rolü "hesap grubu" olan başka öneri (tek hesap grubu kuralı §3.2).
 */
export function hasOtherCalcGroup(
  suggestions: readonly BreakdownSuggestion[],
  indicators: readonly SetNode[],
  parentCode: string,
  key: string,
): boolean {
  const self = suggestions.find((s) => s.key === key);
  const parent = indicators.find((i) => i.code === parentCode);
  const inSet =
    parent !== undefined &&
    indicators.some(
      (i) =>
        i.parentId === parent.id &&
        i.nodeKind === "breakdown_group" &&
        i.breakdown?.dimension !== self?.dimension &&
        isCalculationGroupCandidate({
          id: i.id,
          parentId: i.parentId,
          orderIndex: 0,
          nodeKind: "breakdown_group",
          breakdown: i.breakdown ?? null,
        }),
    );
  return (
    inSet ||
    suggestions.some(
      (s) =>
        s.key !== key &&
        s.parentCode === parentCode &&
        s.state === "accepted" &&
        s.role === "calculation",
    )
  );
}

/**
 * "Bu üyeler tüm kapsamı temsil ediyor" işaretlenince YENİ grubun toplama kuralı (fix round 1 I1):
 * `rollup === "none"` iken birim varsayılanına (`defaultBreakdownRollup`) yükselir ancak varsayılan
 * "none" değilse (oran/yüzde birimleri "none" kalır) ve dosyadaki toplam görünür biçimde
 * çelişmiyorsa (toplam satırı yoksa yükselir). Üstte başka hesap grubu olması artık engel DEĞİL
 * (TODO-KPI-133 (a)): ikinci tam `sum` grup doğrulama grubudur — hesap grubu sıra kuralıyla seçilir.
 * Aksi `null` (değişmez → "yalnız gösterim"). Kutu kaldırılınca ve mevcut grupta hiç değişmez.
 */
export function rollupOnComplete(
  s: BreakdownSuggestion,
  ctx: { parentUnit: string | null },
): Rollup | null {
  if (s.existingGroup || s.rollup !== "none") return null;
  const fallback = defaultBreakdownRollup(ctx.parentUnit);
  if (fallback === "none") return null;
  if (s.columns.length > 0 && !s.totalMatched) return null;
  return fallback;
}

/** Kutu yazımı: `complete` + (işaretlenince, kurala göre) `rollup`. */
export function toggleSuggestionComplete(
  mapping: readonly KpiMappingRow[],
  suggestions: readonly BreakdownSuggestion[],
  indicators: readonly SetNode[],
  key: string,
  complete: boolean,
): KpiMappingRow[] {
  const next = setSuggestionComplete(mapping, key, complete);
  const s = suggestions.find((x) => x.key === key);
  if (!complete || !s) return next;
  const rollup = rollupOnComplete(s, {
    parentUnit: indicators.find((i) => i.code === s.parentCode)?.unit ?? null,
  });
  return rollup ? setSuggestionRollup(next, key, rollup) : next;
}

// İçe aktarma sihirbazı × kırılım (spec §7.3, §18 DR7) — SAF. Eşleştirme satırlarındaki
// `create_breakdown` önerilerinden "Önerilen kırılımlar" tablosunun satırları (üst gösterge ×
// boyut), Önerildi / Kabul edildi / Atlandı geçişleri, alt satır alanlarının (Üst gösterge /
// Grup / Üye adı) `breakdown` alanına yazımı, kaydedilebilir eşleme ve dosyadaki toplam satırının
// yıl başına doğrulaması. Durum sihirbazda kalır (store'a girmez); bileşenler yalnız görünüm.
//
// Kurallar:
//  • Öneri satırı = `breakdown` dolu ∧ eylem `create_breakdown` ya da `skip` (atlanan öneri
//    tabloda "Atlandı" kalır — sunucu `skip` satırındaki `breakdown`'ı okumaz).
//  • "Dosyadaki toplam eşleşti" (otomatik, dosyadan) ≠ "Bu üyeler tüm kapsamı temsil ediyor"
//    (kullanıcı, `breakdown.complete`) — ikisi asla birleştirilmez.
//  • Eksik alanlı `create_breakdown` satırı taslağa `breakdown`'sız gider (şema min(1) — 422
//    olmasın); satır işaretli kalır, plan onu "üst gösterge sette yok" gerekçesiyle düşürür.

import {
  KPI_BREAKDOWN_DIMENSIONS,
  KPI_BREAKDOWN_DIMENSION_LABELS,
  defaultBreakdownRollup,
  formatKpiNumber,
  isCalculationGroupCandidate,
  parseCell,
  slugifyKpiCode,
  stripParentPrefix,
  type KpiBreakdown,
  type KpiImportColumn,
  type KpiImportRow,
  type KpiMappingBreakdown,
  type KpiMappingRow,
  type KpiSetIndicator,
} from "@mahir/shared";
import {
  breakdownRole,
  calcRoleFor,
  type BreakdownRole,
  type CalcRoleRow,
} from "./kpi-import-breakdown-roles";
import { KPI_IMPORT_COPY } from "./kpi-import-copy";
import { type SearchSelectOption } from "./search-select";

const MANUAL = KPI_IMPORT_COPY.mapping.manualNote;
const OTHER = "diger";
const EPS = 1e-9;

type Rollup = KpiBreakdown["rollup"];
type SetNode = Pick<
  KpiSetIndicator,
  "id" | "code" | "label" | "unit" | "parentId" | "nodeKind" | "dimensionMember" | "breakdown"
>;

// ─── Alanlar ────────────────────────────────────────────────────────────────

export type BreakdownFieldProblem = "parent" | "dimension" | "memberLabel";

/** Alt satırın ilk eksik alanı (null = tam). */
export function breakdownProblem(
  bd: Partial<KpiMappingBreakdown> | undefined,
): BreakdownFieldProblem | null {
  if (!bd?.parentCode) return "parent";
  if (!bd.dimension) return "dimension";
  if (!bd.memberLabel?.trim() || !bd.member) return "memberLabel";
  return null;
}

const isFilled = (bd: KpiMappingBreakdown | undefined): bd is KpiMappingBreakdown =>
  breakdownProblem(bd) === null;

/** Boyut seçicinin değeri: listedeki boyut ya da (serbest slug) "diger". */
export function dimensionSelectValue(dimension: string): string {
  if (dimension === "") return "";
  return (KPI_BREAKDOWN_DIMENSIONS as readonly string[]).includes(dimension) ? dimension : OTHER;
}

export function dimensionLabel(dimension: string, groupLabel: string): string {
  if (groupLabel.trim() !== "") return groupLabel;
  const known =
    KPI_BREAKDOWN_DIMENSION_LABELS[dimension as keyof typeof KPI_BREAKDOWN_DIMENSION_LABELS];
  return known ?? dimension;
}

/** Üst gösterge seçenekleri: kategori / grup / üye ve grup dışı çocuklu düğüm hedef olamaz. */
export function breakdownParentOptions(indicators: readonly SetNode[]): SearchSelectOption[] {
  const byId = new Map(indicators.map((i) => [i.id, i]));
  const nonGroupParents = new Set(
    indicators
      .filter((i) => i.parentId && i.nodeKind !== "breakdown_group")
      .map((i) => i.parentId!),
  );
  const pathOf = (i: SetNode): string => {
    const parts: string[] = [];
    let cur = i.parentId ? byId.get(i.parentId) : undefined;
    let guard = 0;
    while (cur && guard < 16) {
      parts.unshift(cur.label);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
      guard += 1;
    }
    return parts.join(" › ");
  };
  return indicators
    .filter(
      (i) =>
        i.nodeKind !== "category" &&
        i.nodeKind !== "breakdown_group" &&
        !i.dimensionMember &&
        !nonGroupParents.has(i.id),
    )
    .map((i) => {
      const path = pathOf(i);
      return { value: i.code, label: i.label, code: i.code, ...(path ? { hint: path } : {}) };
    });
}

/** Satır "Kırılım olarak ekle"ye çevrildiğinde başlangıç alanları (öneri yoksa). */
export function breakdownDraft(
  row: KpiMappingRow,
  parent: { code: string; label: string } | null,
): KpiMappingBreakdown {
  if (row.breakdown) return row.breakdown;
  const memberLabel = parent ? stripParentPrefix(parent.label, row.rowLabel) : row.rowLabel;
  return {
    parentCode: parent?.code ?? "",
    dimension: "",
    groupLabel: "",
    member: memberSlug(memberLabel),
    memberLabel,
  };
}

export function memberSlug(label: string): string {
  return label.trim() === "" ? "" : slugifyKpiCode(label).slice(0, 64);
}

export interface BreakdownFieldPatch {
  parent?: { code: string; label: string };
  /** Boyut seçici değeri (liste ya da "diger"). */
  dimension?: string;
  /** "Diğer" boyutta grup adı (boyut kodu adından üretilir). */
  groupLabel?: string;
  memberLabel?: string;
}

/**
 * Alt satır alanlarını `breakdown`'a yazar: boyut listeden → grup adı boyut etiketi; "Diğer" →
 * grup adı serbest, boyut kodu adından; üye adı → üye slug'ı. Üst değişince üye adı (kullanıcı
 * dokunmadıysa = kaynak etiketi) yeni üstün önekinden soyulur. Satır elle karar sayılır.
 */
export function patchBreakdownFields(
  row: KpiMappingRow,
  patch: BreakdownFieldPatch,
): KpiMappingRow {
  const cur = breakdownDraft(row, null);
  const next: KpiMappingBreakdown = { ...cur };
  if (patch.parent) {
    next.parentCode = patch.parent.code;
    if (cur.memberLabel === row.rowLabel || cur.memberLabel.trim() === "") {
      next.memberLabel = stripParentPrefix(patch.parent.label, row.rowLabel);
      next.member = memberSlug(next.memberLabel);
    }
  }
  if (patch.dimension !== undefined) {
    if (patch.dimension === OTHER) {
      const label = dimensionSelectValue(cur.dimension) === OTHER ? cur.groupLabel : "";
      next.groupLabel = label;
      next.dimension = label.trim() ? otherSlug(label) : OTHER;
    } else {
      next.dimension = patch.dimension;
      next.groupLabel = patch.dimension ? dimensionLabel(patch.dimension, "") : "";
    }
  }
  if (patch.groupLabel !== undefined) {
    next.groupLabel = patch.groupLabel.slice(0, 200);
    next.dimension = patch.groupLabel.trim() ? otherSlug(patch.groupLabel) : OTHER;
  }
  if (patch.memberLabel !== undefined) {
    next.memberLabel = patch.memberLabel.slice(0, 200);
    next.member = memberSlug(patch.memberLabel);
  }
  return {
    rowId: row.rowId,
    rowLabel: row.rowLabel,
    action: "create_breakdown",
    note: MANUAL,
    breakdown: next,
  };
}

function otherSlug(label: string): string {
  return slugifyKpiCode(label).slice(0, 40).replace(/_+$/u, "") || OTHER;
}

/** Taslak/önizleme/commit gövdesi: eksik alanlı kırılım satırı `breakdown`'sız gider. */
export function savableMapping(rows: readonly KpiMappingRow[]): KpiMappingRow[] {
  let changed = false;
  const out = rows.map((r) => {
    if (!r.breakdown || isFilled(r.breakdown)) return r;
    changed = true;
    const { breakdown: _drop, ...rest } = r;
    return rest;
  });
  return changed ? out : [...rows];
}

// ─── Dosyadaki toplam satırı (yıl başına) ───────────────────────────────────

export interface TotalCheckColumn {
  label: string;
  total: number;
  sum: number;
  matched: boolean;
}

/**
 * Toplam satırı = üye satırlarının toplamı mı — sunucu `totalEqualsMembers` ile aynı kural
 * (sayı/yüzde sütunları; üyede boş/"-" 0; metin/"XX" üye → o sütun doğrulanamaz, listede yok).
 */
export function totalCheck(
  total: KpiImportRow,
  members: readonly KpiImportRow[],
  columns: readonly KpiImportColumn[],
): TotalCheckColumn[] {
  if (members.length === 0) return [];
  const out: TotalCheckColumn[] = [];
  for (const c of columns) {
    if (c.kind !== "year" && c.kind !== "entity") continue;
    const own = parseCell(total.cells[c.index]);
    if (own.kind !== "number" && own.kind !== "percent") continue;
    let sum = 0;
    let ok = true;
    for (const m of members) {
      const p = parseCell(m.cells[c.index]);
      if (p.kind === "number" || p.kind === "percent") sum += p.value;
      else if (p.kind !== "empty" && p.kind !== "dash") ok = false;
    }
    if (!ok) continue;
    out.push({
      label: c.kind === "year" ? (c.year?.toString() ?? "") : (c.entityName ?? ""),
      total: own.value,
      sum,
      matched: Math.abs(sum - own.value) <= EPS * Math.max(1, Math.abs(own.value)),
    });
  }
  return out;
}

/** "sistem hesaplar — kaynak 1.235 = alt kalemler ✓" ya da amber fark satırı (son sütun). */
export function totalLine(cols: readonly TotalCheckColumn[]): {
  matched: boolean;
  text: string;
} | null {
  const last = cols[cols.length - 1];
  if (!last) return null;
  const b = KPI_IMPORT_COPY.breakdown;
  if (cols.every((c) => c.matched)) {
    return { matched: true, text: b.systemComputesBadge(formatKpiNumber(last.total)) };
  }
  const bad = [...cols].reverse().find((c) => !c.matched) ?? last;
  return {
    matched: false,
    text: b.totalMismatchLine(
      formatKpiNumber(bad.total),
      formatKpiNumber(bad.sum),
      formatKpiNumber(bad.total - bad.sum),
    ),
  };
}

// ─── Öneri tablosu ──────────────────────────────────────────────────────────

export type SuggestionState = "suggested" | "accepted" | "skipped";

export interface BreakdownSuggestion {
  key: string;
  parentCode: string;
  parentLabel: string;
  dimension: string;
  groupLabel: string;
  rowIds: string[];
  members: string[];
  state: SuggestionState;
  /** "Bu üyeler tüm kapsamı temsil ediyor" (kullanıcı / sunucu önerisi). */
  complete: boolean;
  rollup: Rollup;
  role: BreakdownRole;
  /** Grup bu sette zaten var — metaveri katalogdan, onay kutusu kilitli. */
  existingGroup: boolean;
  /** Dosyadaki toplam satırı (üst göstergeye bağlı, aynı sayfa); yoksa null. */
  totalRowId: string | null;
  /** Yıl (ya da işletme) başına toplam = üyeler; toplam satırı yoksa boş. */
  columns: TotalCheckColumn[];
  /** "Dosyadaki toplam eşleşti" — otomatik, yalnız dosyadan. */
  totalMatched: boolean;
}

export interface SuggestionContext {
  gridRows: ReadonlyMap<string, KpiImportRow>;
  columnsOfRow: (rowId: string) => readonly KpiImportColumn[];
  sheetOfRow: ReadonlyMap<string, number>;
  labelByCode: ReadonlyMap<string, string>;
  indicators: readonly SetNode[];
}

const isSuggestionRow = (
  r: KpiMappingRow,
): r is KpiMappingRow & { breakdown: KpiMappingBreakdown } =>
  (r.action === "create_breakdown" || r.action === "skip") && isFilled(r.breakdown);

export function suggestionKey(bd: Pick<KpiMappingBreakdown, "parentCode" | "dimension">): string {
  return `${bd.parentCode}|${bd.dimension}`;
}

export function breakdownSuggestions(
  mapping: readonly KpiMappingRow[],
  ctx: SuggestionContext,
): BreakdownSuggestion[] {
  const byKey = new Map<string, KpiMappingRow[]>();
  for (const r of mapping) {
    if (!isSuggestionRow(r)) continue;
    const k = suggestionKey(r.breakdown);
    const list = byKey.get(k);
    if (list) list.push(r);
    else byKey.set(k, [r]);
  }
  const byCode = new Map(ctx.indicators.map((i) => [i.code, i]));
  const out: BreakdownSuggestion[] = [];
  /** Üst kodu → setteki BAŞKA bir aday grup hesap grubunu taşıyor (rol: doğrulama). */
  const externalCalc = new Set<string>();
  for (const [key, rows] of byKey) {
    const bd = rows[0]!.breakdown!;
    const parent = byCode.get(bd.parentCode);
    const group = parent
      ? ctx.indicators.find(
          (i) =>
            i.parentId === parent.id &&
            i.nodeKind === "breakdown_group" &&
            i.breakdown?.dimension === bd.dimension,
        )
      : undefined;
    const complete = group?.breakdown
      ? group.breakdown.complete
      : rows.every((r) => r.breakdown?.complete === true);
    const rollup: Rollup =
      group?.breakdown?.rollup ??
      rows.find((r) => r.breakdown?.rollup)?.breakdown?.rollup ??
      defaultBreakdownRollup(parent?.unit ?? null);
    const state: SuggestionState = rows.every((r) => r.action === "skip")
      ? "skipped"
      : rows.every((r) => r.action === "create_breakdown" && r.note === MANUAL)
        ? "accepted"
        : "suggested";
    const members = [...new Set(rows.map((r) => r.breakdown!.memberLabel))];
    if (
      parent !== undefined &&
      ctx.indicators.some(
        (i) =>
          i.parentId === parent.id &&
          i.nodeKind === "breakdown_group" &&
          i.breakdown?.dimension !== bd.dimension &&
          isCalculationGroupCandidate({
            id: i.id,
            parentId: i.parentId,
            orderIndex: 0,
            nodeKind: "breakdown_group",
            breakdown: i.breakdown ?? null,
          }),
      )
    ) {
      externalCalc.add(bd.parentCode);
    }
    const { totalRowId, columns } = fileTotalOf(mapping, rows, bd.parentCode, ctx);
    out.push({
      key,
      parentCode: bd.parentCode,
      parentLabel: parent?.label ?? ctx.labelByCode.get(bd.parentCode) ?? bd.parentCode,
      dimension: bd.dimension,
      groupLabel: group?.label ?? dimensionLabel(bd.dimension, bd.groupLabel),
      rowIds: rows.map((r) => r.rowId),
      members,
      state,
      complete,
      rollup,
      role: "none",
      existingGroup: group !== undefined,
      totalRowId,
      columns,
      totalMatched: columns.length > 0 && columns.every((c) => c.matched),
    });
  }
  // rol: kabul edilmiş + önerilen (atlanmamış) kümeden tek kuralla (TODO-KPI-121)
  const roles = calcRoleFor(
    out.map((o) => ({
      key: o.key,
      parentCode: o.parentCode,
      dimension: o.dimension,
      complete: o.complete,
      rollup: o.rollup,
    })),
    new Set(out.filter((o) => o.state !== "skipped").map((o) => o.key)),
    externalCalc,
  );
  for (const o of out) {
    const r = roles.get(o.key);
    o.role = r === "calc" ? "calculation" : r === "verify" ? "check" : "none";
  }
  return out;
}

/** Üst göstergeye bağlı (öneri dışı) en yakın satır, aynı sayfa → yıl başına doğrulama. */
function fileTotalOf(
  mapping: readonly KpiMappingRow[],
  members: readonly KpiMappingRow[],
  parentCode: string,
  ctx: SuggestionContext,
): { totalRowId: string | null; columns: TotalCheckColumn[] } {
  const first = members[0]!;
  const sheet = ctx.sheetOfRow.get(first.rowId);
  const memberIds = new Set(members.map((m) => m.rowId));
  const index = new Map(mapping.map((r, i) => [r.rowId, i]));
  const at = index.get(first.rowId) ?? 0;
  // Aynı sayfada üste bağlı TÜM aday toplam satırları (iki blok aynı üstü taşıyabilir), yakından uzağa
  const candidates = mapping
    .filter(
      (r) =>
        !memberIds.has(r.rowId) &&
        r.targetCode === parentCode &&
        ctx.sheetOfRow.get(r.rowId) === sheet &&
        ctx.gridRows.has(r.rowId),
    )
    .sort(
      (a, b) => Math.abs((index.get(a.rowId) ?? 0) - at) - Math.abs((index.get(b.rowId) ?? 0) - at),
    );
  if (candidates.length === 0) return { totalRowId: null, columns: [] };
  // Atlanan grupta da tamlık gösterilir (geri alınca ne olacağı); aksi yalnız eklenecek üyeler
  const active = members.filter((m) => m.action === "create_breakdown");
  const counted = (active.length > 0 ? active : members)
    .map((m) => ctx.gridRows.get(m.rowId))
    .filter((g): g is KpiImportRow => g !== undefined);
  const checked = candidates.map((r) => ({
    rowId: r.rowId,
    columns: totalCheck(ctx.gridRows.get(r.rowId)!, counted, ctx.columnsOfRow(r.rowId)),
  }));
  // Sunucu kuralı (`totals.some`): üye toplamına EŞİT olan ilk toplam satırı; yoksa en yakın
  const hit =
    checked.find((c) => c.columns.length > 0 && c.columns.every((x) => x.matched)) ?? checked[0]!;
  return { totalRowId: hit.rowId, columns: hit.columns };
}

export type SuggestionDecision = "accept" | "skip" | "undo";

/**
 * Grup bazında karar: kabul → satırlar `create_breakdown` + elle karar (güven/hafıza izi düşer);
 * atla → `skip`, `breakdown` korunur (tabloda "Atlandı" kalır, geri alınabilir); geri al → ilk
 * öneri (sihirbaz açılışındaki satır).
 */
export function decideSuggestions(
  mapping: readonly KpiMappingRow[],
  keys: ReadonlySet<string>,
  decision: SuggestionDecision,
  initial: ReadonlyMap<string, KpiMappingRow>,
): KpiMappingRow[] {
  return mapping.map((r) => {
    if (!isSuggestionRow(r) || !keys.has(suggestionKey(r.breakdown))) return r;
    if (decision === "undo") {
      const first = initial.get(r.rowId);
      return first?.breakdown ? first : { ...r, action: "create_breakdown" as const };
    }
    return {
      rowId: r.rowId,
      rowLabel: r.rowLabel,
      action: decision === "accept" ? ("create_breakdown" as const) : ("skip" as const),
      note: MANUAL,
      breakdown: r.breakdown,
    };
  });
}

/** "Bu üyeler tüm kapsamı temsil ediyor" — grubun tüm satırlarına `complete`. */
export function setSuggestionComplete(
  mapping: readonly KpiMappingRow[],
  key: string,
  complete: boolean,
): KpiMappingRow[] {
  return mapping.map((r) =>
    isSuggestionRow(r) && suggestionKey(r.breakdown) === key
      ? { ...r, breakdown: { ...r.breakdown, complete } }
      : r,
  );
}

/**
 * Eşleştirme tablosu: toplam satırı → rozet metni (eşit) ya da amber fark satırı. Yalnız kapsamı
 * onaylı (`complete`) gruplar sayılır — "yalnız gösterim" grubunun toplamı tutmaması beklenir.
 * Aynı toplamın birden çok tam grubu varsa hepsi eşit olmalı; ilk farklı grup amber satırı verir.
 */
export function totalRowLines(
  suggestions: readonly BreakdownSuggestion[],
): Map<string, { matched: boolean; text: string }> {
  const out = new Map<string, { matched: boolean; text: string }>();
  for (const s of suggestions) {
    if (!s.totalRowId || s.state === "skipped" || !s.complete) continue;
    const line = totalLine(s.columns);
    if (!line) continue;
    const prev = out.get(s.totalRowId);
    if (!prev || (prev.matched && !line.matched)) out.set(s.totalRowId, line);
  }
  return out;
}

export { breakdownRole, calcRoleFor, type BreakdownRole, type CalcRoleRow };
